import React from "react";
import { features } from "../../data/mockData";
import { Save, FolderOpen, Users, Check, Plus, Search, MessageCircle, Tag } from "lucide-react";

const featureIcons = {
  Sauvegardez: Save,
  Organisez: FolderOpen,
  Collaborez: Users,
};

// Preview component for each feature
const FeaturePreview = ({ feature }) => {
  if (feature.tag === "Sauvegardez") {
    return (
      <div className="bg-[#0c0c0e] rounded-xl border border-white/[0.08] p-4 h-full">
        <div className="flex items-center justify-between mb-4">
          <span className="text-[11px] text-white/50">Ajouter un contenu</span>
          <div className="w-6 h-6 rounded-lg bg-white/[0.06] flex items-center justify-center">
            <Plus size={12} className="text-white/40" />
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] text-white/30 mb-1">TITRE</div>
            <div className="bg-white/[0.04] rounded-lg px-3 py-2 text-[11px] text-white/60">Nike - Just Do It</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] text-white/30 mb-1">TYPE</div>
              <div className="bg-white/[0.04] rounded-lg px-3 py-2 text-[11px] text-white/60">Ad</div>
            </div>
            <div>
              <div className="text-[10px] text-white/30 mb-1">PLATEFORME</div>
              <div className="bg-white/[0.04] rounded-lg px-3 py-2 text-[11px] text-white/60">Facebook, Instagram</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] text-white/30 mb-1">NOTE INITIALE</div>
            <div className="bg-white/[0.04] rounded-lg px-3 py-2 text-[11px] text-white/50 h-16">Super hook d'accroche, à réutiliser...</div>
          </div>
        </div>
      </div>
    );
  }
  
  if (feature.tag === "Organisez") {
    return (
      <div className="bg-[#0c0c0e] rounded-xl border border-white/[0.08] p-4 h-full">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex-1 bg-white/[0.04] rounded-lg px-3 py-2 flex items-center gap-2">
            <Search size={12} className="text-white/30" />
            <span className="text-[11px] text-white/30">Rechercher...</span>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-4">
          <div className="bg-white/[0.06] rounded-lg px-2.5 py-1.5 text-[10px] text-white/60">Platform</div>
          <div className="bg-white/[0.06] rounded-lg px-2.5 py-1.5 text-[10px] text-white/60">Tags</div>
          <div className="bg-white/[0.06] rounded-lg px-2.5 py-1.5 text-[10px] text-white/60">Category</div>
          <div className="bg-white/[0.06] rounded-lg px-2.5 py-1.5 text-[10px] text-white/60">Type</div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {["#hook", "#video", "#ugc", "#facebook", "#conversion"].map((tag, i) => (
            <span key={i} className="bg-white/[0.04] rounded px-2 py-1 text-[10px] text-white/50">{tag}</span>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="aspect-square rounded-lg bg-gradient-to-br from-white/[0.04] to-white/[0.02] border border-white/[0.06]" />
          ))}
        </div>
      </div>
    );
  }
  
  if (feature.tag === "Collaborez") {
    return (
      <div className="bg-[#0c0c0e] rounded-xl border border-white/[0.08] p-4 h-full">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle size={14} className="text-white/40" />
          <span className="text-[11px] text-white/50">Commentaires</span>
        </div>
        <div className="space-y-3">
          {[
            { user: "M", name: "Marie", time: "Il y a 2h", text: "Super pour le client Acme !" },
            { user: "T", name: "Thomas", time: "Il y a 1h", text: "On pourrait adapter le hook" },
            { user: "S", name: "Sophie", time: "Il y a 30min", text: "Ajouté au board Q1" },
          ].map((comment, i) => (
            <div key={i} className="flex gap-2.5">
              <div className="w-6 h-6 rounded-full bg-white/[0.08] flex items-center justify-center text-[10px] text-white/50 shrink-0">
                {comment.user}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-white/70">{comment.name}</span>
                  <span className="text-[9px] text-white/30">{comment.time}</span>
                </div>
                <p className="text-[11px] text-white/50 mt-0.5">{comment.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return null;
};

const HowItWorksSection = () => {
  return (
    <section id="features" className="py-28 relative">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section Header */}
        <div className="text-center max-w-xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 bg-white/[0.03] border border-white/[0.07] rounded-full px-4 py-1.5 mb-6">
            <span className="text-[11px] text-[#7a7a7a] uppercase tracking-widest font-medium">Fonctionnalités</span>
          </div>
          <h2 className="text-3xl md:text-[2.75rem] font-bold text-white tracking-[-0.02em] leading-tight mb-4">
            Simple. Puissant. Efficace.
          </h2>
          <p className="text-[#666] text-base leading-relaxed">
            Tout ce dont vous avez besoin pour construire votre bibliothèque créative.
          </p>
        </div>

        {/* Feature Cards with Previews */}
        <div className="space-y-8">
          {features.map((feature, index) => {
            const Icon = featureIcons[feature.tag];
            const isReversed = index % 2 === 1;

            return (
              <div
                key={feature.id}
                className={`grid lg:grid-cols-2 gap-0 rounded-2xl border border-white/[0.06] bg-[#0a0a0c] overflow-hidden group hover:border-white/[0.1] transition-all duration-500`}
              >
                {/* Content */}
                <div className={`p-10 lg:p-12 flex flex-col justify-center ${isReversed ? "lg:order-2" : ""}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-xl bg-white/[0.05] border border-white/[0.08] flex items-center justify-center">
                      {Icon && <Icon size={17} className="text-white/60" />}
                    </div>
                    <span className="text-[11px] text-[#666] uppercase tracking-[0.15em] font-medium">{feature.tag}</span>
                  </div>

                  <h3 className="text-2xl md:text-[1.75rem] text-white font-bold tracking-[-0.02em] mb-4 leading-tight">
                    {feature.title}
                  </h3>
                  <p className="text-[#666] text-[15px] leading-relaxed mb-6">
                    {feature.description}
                  </p>

                  <div className="space-y-3">
                    {feature.bullets.map((bullet, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full bg-white/[0.05] flex items-center justify-center shrink-0">
                          <Check size={11} className="text-emerald-400/80" />
                        </div>
                        <span className="text-[13px] text-[#888]">{bullet}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Preview Mockup */}
                <div className={`relative p-8 bg-[#080809] flex items-center justify-center ${isReversed ? "lg:order-1" : ""}`}>
                  <div className="w-full max-w-[340px]">
                    <FeaturePreview feature={feature} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
